import { lazy } from 'react';
import { UserRole } from '../generated/graphql';
import CreateEditDining from '../containers/contentManagement/CreateEditDining';
import CreateEditService from '../containers/contentManagement/CreateEditService';
import ContactUsManagement from '../containers/contactUs/ContactUsManagement';
import CreateContact from '../containers/contactUs/CreateContact';
import CreateEditContactUs from '../containers/contactUs/CreateEditContactUs';

const AsyncAcceptAdminInvite = lazy(() => import('../containers/users/AcceptAdminInvite'));

const AsyncContentManagement = lazy(() => import('../containers/contentManagement/ContentManagement'));
const AsyncCreatePost = lazy(() => import('../containers/contentManagement/CreateNewPost'));
const AsyncPostDetails = lazy(() => import('../containers/contentManagement/PostDetails'));
const AsyncCreateProperty = lazy(() => import('../containers/properties/CreateNewProperty'));
const AsyncCreateStakeholder = lazy(() => import('../containers/stakeholders/CreateNewStakeholder'));
const AsyncDashboard = lazy(() => import('../containers/dashboard/Dashboard'));
const AsyncProperties = lazy(() => import('../containers/properties/AllProperties'));
const AsyncPropertyDetails = lazy(() => import('../containers/properties/PropertyDetails'));
const AsyncResetPassword = lazy(() => import('../containers/users/ResetPassword'));
const AsyncSignIn = lazy(() => import('../App'));
const AsyncStakeholderDetails = lazy(() => import('../containers/stakeholders/StakeholderDetails'));
const AsyncStakeholders = lazy(() => import('../containers/stakeholders/AllStakeholders'));
const AsyncUsers = lazy(() => import('../containers/users/AllUsers'));
const AsyncSecurityManagement = lazy(() => import('../containers/security/SecurityManagement'));
const AsyncProfile = lazy(() => import('../containers/users/Profile'));
const AsyncForgotPassword = lazy(() => import('../containers/users/ForgotPassword'));
const AsyncCreateNewShopping = lazy(() => import('../containers/contentManagement/CreateEditShop'));
const AsyncCreateNewFacilities = lazy(() => import('../containers/contentManagement/CreateEditFacility'));
const AsyncCreateEvent = lazy(() => import('../containers/contentManagement/CreateEditEvent'));

const AsyncCreateEditEstateInformation = lazy(
  () => import('../containers/contentManagement/CreateEditEstateInformation')
);
export type RouteArray = Array<RouteObject>;

export type RouteObject = {
  title: string;
  path: string;
  component: React.ReactNode | object;
  private: boolean;
  breadcrumb?: string;
};

const PublicRoutes = [
  {
    title: 'Sign In',
    path: '/',
    component: AsyncSignIn,
    exact: true,
    private: false,
  },
  {
    title: 'Accept Admin Invite',
    path: '/validate/:id/:otp',
    component: AsyncAcceptAdminInvite,
    exact: true,
    private: false,
  },
  {
    title: 'Reset Password',
    path: '/reset-password/:id/:otp',
    component: AsyncResetPassword,
    exact: true,
    private: false,
  },
  {
    title: 'Forgot Password',
    path: '/forgot-password/',
    component: AsyncForgotPassword,
    exact: true,
    private: false,
  },
];

const PrivateRoutes = [
  {
    title: 'Dashboard',
    path: '/dashboard',
    component: AsyncDashboard,
    exact: true,
    private: true,
    breadcrumb: 'Dashboard',
    permissions: [UserRole.Admin, UserRole.Superadmin],
  },
  {
    title: 'Properties',
    path: '/properties',
    component: AsyncProperties,
    exact: true,
    private: true,
    breadcrumb: 'Properties',
    permissions: [UserRole.Admin, UserRole.Superadmin, UserRole.StakeholderAdmin],
  },

  {
    title: 'Stakeholders',
    path: '/stakeholders/:step',
    component: AsyncStakeholders,
    exact: true,
    private: true,
    breadcrumb: 'Stakeholders',
    permissions: [UserRole.Admin, UserRole.Superadmin, UserRole.StakeholderAdmin],
  },
  // EVENTS START
  {
    title: 'Create New Event',
    path: '/content-management/events/create-new',
    component: AsyncCreateEvent,
    exact: true,
    private: true,
    breadcrumb: 'Create Event',
    permissions: [UserRole.Admin, UserRole.Superadmin, UserRole.ContentAdmin],
  },
  {
    title: 'Edit Event',
    path: '/content-management/events/:id',
    component: AsyncCreateEvent,
    exact: true,
    private: true,
    breadcrumb: 'Edit Event',
    permissions: [UserRole.Admin, UserRole.Superadmin, UserRole.ContentAdmin],
  },
  {
    title: 'Create Dining',
    path: '/content-management/dining/create-new',
    component: CreateEditDining,
    exact: true,
    private: true,
    breadcrumb: 'Create Dining',
    permissions: [UserRole.Admin, UserRole.Superadmin, UserRole.ContentAdmin],
  },
  {
    title: 'Edit Dining',
    path: '/content-management/dining/:id/details',
    component: CreateEditDining,
    exact: true,
    private: true,
    breadcrumb: 'Edit Dining',
    permissions: [UserRole.Admin, UserRole.Superadmin, UserRole.ContentAdmin],
  },
  {
    title: 'Create New Shop',
    path: '/content-management/shopping/create-new',
    component: AsyncCreateNewShopping,
    exact: true,
    private: true,
    breadcrumb: 'Create Shop',
    permissions: [UserRole.Admin, UserRole.Superadmin, UserRole.ContentAdmin],
  },
  {
    title: 'Edit Shop',
    path: '/content-management/shopping/:id/details',
    component: AsyncCreateNewShopping,
    exact: true,
    private: true,
    breadcrumb: 'Edit Shop',
    permissions: [UserRole.Admin, UserRole.Superadmin, UserRole.ContentAdmin],
  },
  {
    title: 'Create New Facility',
    path: '/content-management/facilities/create-new',
    component: AsyncCreateNewFacilities,
    exact: true,
    private: true,
    breadcrumb: 'Create Facility',
    permissions: [UserRole.Admin, UserRole.Superadmin, UserRole.ContentAdmin],
  },
  {
    title: 'Edit Facility',
    path: '/content-management/facilities/:id/details',
    component: AsyncCreateNewFacilities,
    exact: true,
    private: true,
    breadcrumb: 'Edit Facility',
    permissions: [UserRole.Admin, UserRole.Superadmin, UserRole.ContentAdmin],
  },
  {
    title: 'Create New Service',
    path: '/content-management/services/create-new',
    component: CreateEditService,
    exact: true,
    private: true,
    breadcrumb: 'Create Service',
    permissions: [UserRole.Admin, UserRole.Superadmin, UserRole.ContentAdmin],
  },
  {
    title: 'Edit Service',
    path: '/content-management/services/:id/details',
    component: CreateEditService,
    exact: true,
    private: true,
    breadcrumb: 'Edit Service',
    permissions: [UserRole.Admin, UserRole.Superadmin, UserRole.ContentAdmin],
  },
  {
    title: 'Create New Post',
    path: '/content-management/:step/create-new',
    component: AsyncCreatePost,
    exact: true,
    private: true,
    breadcrumb: 'Create Post',
    permissions: [UserRole.Admin, UserRole.Superadmin, UserRole.ContentAdmin],
  },
  {
    title: 'Update Post',
    path: '/content-management/:id/details',
    component: AsyncPostDetails,
    exact: true,
    private: true,
    breadcrumb: 'Update Post',
    permissions: [UserRole.Admin, UserRole.Superadmin, UserRole.ContentAdmin],
  },
  {
    title: 'Content Management',
    path: '/content-management/:step',
    component: AsyncContentManagement,
    exact: true,
    private: true,
    breadcrumb: 'Content Management',
    permissions: [UserRole.Admin, UserRole.Superadmin, UserRole.ContentAdmin],
  },
  {
    title: 'User Management',
    path: '/user-management',
    component: AsyncUsers,
    exact: true,
    private: true,
    breadcrumb: 'User Management',
    permissions: [UserRole.Admin, UserRole.Superadmin],
  },
  {
    title: 'Security Management',
    path: '/security-management',
    component: AsyncSecurityManagement,
    exact: true,
    private: true,
    breadcrumb: 'Security Management',
    permissions: [UserRole.Admin, UserRole.Superadmin, UserRole.SecurityAdmin],
  },
  {
    title: 'Create New Property',
    path: '/properties/create-new/:step',
    component: AsyncCreateProperty,
    exact: true,
    private: true,
    breadcrumb: 'Create New Property',
    permissions: [UserRole.Admin, UserRole.Superadmin, UserRole.StakeholderAdmin],
  },
  {
    title: 'Properties',
    path: '/properties/:propertyID/:step',
    component: AsyncPropertyDetails,
    exact: true,
    private: true,
    breadcrumb: 'Property Details',
    permissions: [UserRole.Admin, UserRole.Superadmin, UserRole.StakeholderAdmin],
  },
  // START STAKEHOLDERS
  {
    title: 'Create New Stakeholder',
    path: '/stakeholders/:step/create-new/',
    component: AsyncCreateStakeholder,
    exact: true,
    private: true,
    breadcrumb: 'Create Stakeholder',
    permissions: [UserRole.Admin, UserRole.Superadmin, UserRole.StakeholderAdmin],
  },
  {
    title: 'Stakeholder',
    path: '/stakeholders/:stakeholderID/:step',
    component: AsyncStakeholderDetails,
    exact: true,
    private: true,
    breadcrumb: 'Stakeholder Details',
    permissions: [UserRole.Admin, UserRole.Superadmin, UserRole.StakeholderAdmin],
  },
  {
    title: 'Profile',
    path: '/profile/:userID',
    component: AsyncProfile,
    exact: true,
    private: true,
    breadcrumb: 'Profile',
    permissions: [UserRole.Admin, UserRole.Superadmin, UserRole.VendorAdmin, UserRole.VendorUser],
  },
  {
    title: 'Contact Us Management',
    path: '/contact-us',
    component: ContactUsManagement,
    exact: true,
    private: true,
    breadcrumb: 'Contact Us Management',
    permissions: [UserRole.Admin, UserRole.Superadmin, UserRole.ContentAdmin],
  },
  {
    title: 'Create Contact',
    path: '/contact-us/create-new',
    component: CreateContact,
    exact: true,
    private: true,
    breadcrumb: 'Create Contact',
    permissions: [UserRole.Admin, UserRole.Superadmin, UserRole.ContentAdmin],
  },
  {
    title: 'Edit Contact',
    path: '/contact-us/:id/details',
    component: CreateEditContactUs,
    exact: true,
    private: true,
    breadcrumb: 'Edit Contact',
    permissions: [UserRole.Admin, UserRole.Superadmin, UserRole.ContentAdmin],
  },
  {
    title: 'Create Estate Information',
    path: '/content-management/estate-policies-procedures/create',
    component: AsyncCreateEditEstateInformation,
    exact: true,
    private: true,
    breadcrumb: 'Create Estate Information',
    permissions: [UserRole.Admin, UserRole.Superadmin, UserRole.ContentAdmin],
  },
  {
    title: 'Edit Estate Information',
    path: '/content-management/estate-policies-procedures/:id/details',
    component: AsyncCreateEditEstateInformation,
    exact: true,
    private: true,
    breadcrumb: 'Edit Estate Information',
    permissions: [UserRole.Admin, UserRole.Superadmin, UserRole.ContentAdmin],
  },
];

export { PublicRoutes, PrivateRoutes };



// WEBPACK FOOTER //
// ./src/navigation/Routes.tsx